import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../api';

const ParentMarksView = ({
  studentId,
  academicContext,
  // Props for parent-controlled state
  marksData: externalMarksData,
  marksLoading: externalMarksLoading,
  marksError: externalMarksError,
  onMarksUpdate
}) => {
  // Internal state for independent operation
  const [internalMarksData, setInternalMarksData] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState({
    academicYearId: null,
    programTypeId: null,
    programId: null,
    termId: null,
    semesterId: null
  });
  // State for tracking which subject's attendance is expanded
  const [expandedAttendance, setExpandedAttendance] = useState(null);

  // Use external state if provided, otherwise use internal
  const marksData = externalMarksData || internalMarksData;
  const loading = externalMarksLoading !== undefined ? externalMarksLoading : internalLoading;
  const error = externalMarksError || internalError;

  // Initialize period from academic context
  useEffect(() => {
    if (academicContext?.hierarchy) {
      const h = academicContext.hierarchy;
      setSelectedPeriod({
        academicYearId: h.academicYear?.id || null,
        programTypeId: h.programType?.id || null,
        programId: h.program?.id || null,
        termId: h.term?.id || null,
        semesterId: h.semester?.number || h.semester?.id || null
      });
    }
  }, [academicContext]);

  // API: Fetch marks for the child
  const fetchMarksData = useCallback(async (periodOverride = null) => {
    if (!studentId) {
      console.warn('No studentId provided for marks fetch');
      return;
    }

    try {
      if (externalMarksLoading === undefined) {
        setInternalLoading(true);
      }
      if (externalMarksError !== undefined) {
        // Don't set internal error if external is controlled
      } else {
        setInternalError(null);
      }

      const period = periodOverride || selectedPeriod;
      const { academicYearId, programTypeId, programId, termId, semesterId } = period;

      if (!academicYearId || !programTypeId || !programId) {
        console.warn('Missing required parameters for marks fetch', period);
        if (externalMarksError === undefined) {
          setInternalError('Missing required academic context parameters');
        }
        return;
      }

      const params = new URLSearchParams({
        academicYearId,
        programTypeId,
        programId
      });

      if (termId) {
        params.append('termId', termId);
      } else if (semesterId) {
        params.append('semesterId', semesterId);
      }

      console.log('🔄 Fetching parent marks with params:', params.toString());

      const response = await api.get(`/api/parent/child/${studentId}/marks?${params.toString()}`);
      
      console.log('✅ Parent marks response:', response.data);

      if (response.data.success) {
        const data = response.data.data;
        
        if (externalMarksData === undefined) {
          setInternalMarksData(data);
        }
        
        if (onMarksUpdate) {
          onMarksUpdate(data);
        }
        
        toast.success('Marks loaded successfully');
      } else {
        throw new Error(response.data.message || 'Failed to load marks');
      }
    } catch (err) {
      console.error('❌ Error fetching parent marks:', err);
      const errorMsg = err.response?.data?.message || 'Failed to load marks data';
      
      if (externalMarksError === undefined) {
        setInternalError(errorMsg);
      }
      
      toast.error(errorMsg);
    } finally {
      if (externalMarksLoading === undefined) {
        setInternalLoading(false);
      }
    }
  }, [studentId, selectedPeriod, externalMarksData, externalMarksLoading, externalMarksError, onMarksUpdate]);

  // API: Refresh marks (force recompute)
  const refreshMarksData = useCallback(async () => {
    if (!studentId) return;

    try {
      setInternalLoading(true);
      setInternalError(null);

      const { academicYearId, programTypeId, programId, termId, semesterId } = selectedPeriod;

      if (!academicYearId || !programTypeId || !programId) {
        toast.warning('Missing required parameters for refresh');
        return;
      }

      const response = await api.post(`/api/parent/child/${studentId}/marks/refresh`, {
        academicYearId,
        programTypeId,
        programId,
        ...(termId && { termId }),
        ...(semesterId && { semesterId })
      });

      console.log('✅ Marks refreshed:', response.data);

      if (response.data.success) {
        const data = response.data.data;
        
        setInternalMarksData(data);
        
        if (onMarksUpdate) {
          onMarksUpdate(data);
        }
        
        toast.success('Marks refreshed successfully');
        await fetchMarksData(selectedPeriod);
      } else {
        throw new Error(response.data.message || 'Failed to refresh marks');
      }
    } catch (err) {
      console.error('❌ Error refreshing marks:', err);
      const errorMsg = err.response?.data?.message || 'Failed to refresh marks';
      setInternalError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setInternalLoading(false);
    }
  }, [studentId, selectedPeriod, onMarksUpdate, fetchMarksData]);

  // API: Fetch subject-specific marks
  const fetchSubjectMarks = useCallback(async (subjectId) => {
    if (!studentId || !subjectId) return null;

    try {
      const { academicYearId, programTypeId, programId, termId, semesterId } = selectedPeriod;

      if (!academicYearId || !programTypeId || !programId) {
        toast.warning('Missing required parameters');
        return null;
      }

      const params = new URLSearchParams({
        academicYearId,
        programTypeId,
        programId
      });

      if (termId) {
        params.append('termId', termId);
      } else if (semesterId) {
        params.append('semesterId', semesterId);
      }

      const response = await api.get(
        `/api/parent/child/${studentId}/marks/subject/${subjectId}?${params.toString()}`
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch subject marks');
      }
    } catch (err) {
      console.error('❌ Error fetching subject marks:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch subject marks');
      return null;
    }
  }, [studentId, selectedPeriod]);

  // Auto-fetch on mount and when period changes
  useEffect(() => {
    if (externalMarksData === undefined && studentId) {
      const { academicYearId, programTypeId, programId } = selectedPeriod;
      if (academicYearId && programTypeId && programId) {
        fetchMarksData();
      }
    }
  }, [studentId, selectedPeriod, externalMarksData, fetchMarksData]);

  // Check if subject has attendance marks
  const hasAttendanceMarks = (subject) => {
    if (!subject?.marks) return false;
    return subject.marks.some(m => 
      m.label && (
        m.label.toLowerCase().includes('attendance') ||
        m.label.toLowerCase().includes('attend')
      )
    );
  };

  // Handle subject click to show details
  const handleSubjectClick = async (subject) => {
    try {
      const subjectData = await fetchSubjectMarks(subject.subjectId || subject.id);
      
      if (subjectData) {
        const updatedSubject = {
          ...subject,
          ...subjectData.subject,
          marks: subjectData.subject?.marks || subject.marks || [],
          cieScore: subjectData.subject?.cieScore || subject.cieScore || { obtained: 0, max: 0 }
        };
        
        if (onMarksUpdate) {
          onMarksUpdate({ ...marksData, selectedSubject: updatedSubject });
        }
      }
    } catch (err) {
      console.error('Error fetching subject details:', err);
    }
  };

  // Get period label
  const getPeriodLabel = () => {
    if (!marksData) return '';
    
    const periodType = marksData?.studentInfo?.periodType || '';
    const periodName = marksData?.studentInfo?.periodName || '';
    
    if (periodType === 'term') {
      return periodName || 'Term';
    } else if (periodType === 'semester') {
      return periodName || 'Semester';
    }
    
    return '';
  };

  // Get context display
  const getContextDisplay = () => {
    if (!marksData) return 'Academic Context';
    
    const parts = [];
    
    if (marksData?.studentInfo?.programTypeName) {
      parts.push(marksData.studentInfo.programTypeName);
    }
    if (marksData?.studentInfo?.programName) {
      parts.push(marksData.studentInfo.programName);
    }
    if (marksData?.studentInfo?.academicYearName) {
      parts.push(marksData.studentInfo.academicYearName);
    }
    
    const periodLabel = getPeriodLabel();
    if (periodLabel) {
      parts.push(periodLabel);
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'Academic Context';
  };

  // Attendance Info Expand Component
  const AttendanceInfoExpand = ({ subject, onClose }) => {
    if (!subject) return null;
    
    const attendanceMark = subject.marks?.find(m => 
      m.label && (
        m.label.toLowerCase().includes('attendance') ||
        m.label.toLowerCase().includes('attend')
      )
    );

    return (
      <div className="mt-3 pl-10">
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 border border-indigo-200 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider">Attendance Marks Breakdown</p>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">
                  {attendanceMark ? `${attendanceMark.obtained} / ${attendanceMark.max} Marks` : 'Not Populated'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-2 leading-relaxed">
            <p className="font-semibold text-slate-800">Calculation Protocol Details:</p>
            <ul className="space-y-1 list-disc list-inside text-slate-500">
              <li>Derived systematically from aggregate presence logs for this active session.</li>
              <li>Maps directly against predefined modular calculation metrics.</li>
              <li>Authorized directly through internal evaluation criteria profiles.</li>
              <li>Reflects overall cumulative session inputs recorded up to current sync cycles.</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative flex items-center justify-center">
          <div className="absolute w-11 h-11 rounded-full border-4 border-indigo-100" />
          <div className="w-11 h-11 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" />
        </div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-4">Loading marks data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12 px-4 max-w-sm mx-auto">
        <div className="inline-flex p-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-500 mb-3">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <p className="text-xs font-bold text-slate-800 leading-snug">{error}</p>
        <button
          onClick={refreshMarksData}
          className="mt-4 w-full sm:w-auto px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  // No data state
  if (!marksData || !marksData.subjects || marksData.subjects.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <div className="inline-flex p-4 rounded-xl bg-slate-100 border border-slate-200/60 text-slate-400 mb-3">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-sm font-bold text-slate-700">No Evaluation Records Present</p>
        <p className="text-xs text-slate-400 mt-1">CIE evaluations haven't been finalized for this term block.</p>
        <button
          onClick={refreshMarksData}
          className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm"
        >
          Refresh Data
        </button>
      </div>
    );
  }

  // Main render with inline expansion (single scroll)
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Marks Overview</h2>
              <p className="text-xs text-slate-500">{getContextDisplay()}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-lg">
              <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <span className="text-xs font-semibold text-slate-700">{marksData.subjects.length} Subjects</span>
            </div>
            
            <button
              onClick={refreshMarksData}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg shadow-sm active:scale-95 transition-all"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.253 8H18" />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Subject Cards - Single scroll container */}
      <div className="space-y-3">
        {marksData.subjects.map((subject, index) => {
          const hasCie = subject.cieScore && subject.cieScore.max > 0;
          const hasAttendance = hasAttendanceMarks(subject);
          const isAttendanceExpanded = expandedAttendance === subject.subjectId || expandedAttendance === subject.id;
          
          // Get all mark labels and values
          const markLabels = subject.marks?.map(m => ({
            label: m.label,
            obtained: m.obtained,
            max: m.max
          })) || [];

          return (
            <div 
              key={subject.subjectId || subject.id || index}
              className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
            >
              {/* Subject Header - Clickable */}
              <div 
                className="cursor-pointer"
                onClick={() => handleSubjectClick(subject)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-bold border border-indigo-100">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">
                        {subject.subjectName}
                      </h3>
                      {subject.subjectCode && (
                        <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">
                          {subject.subjectCode}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Overall CIE Score */}
                  {hasCie ? (
                    <div className="flex items-center gap-2 bg-indigo-50 px-3 py-1.5 rounded-lg border border-indigo-100">
                      <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">CIE</span>
                      <span className="text-sm font-bold text-indigo-700">
                        {subject.cieScore.obtained || 0}
                      </span>
                      <span className="text-xs text-indigo-400">/ {subject.cieScore.max}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
                      Not graded
                    </span>
                  )}
                </div>

                {/* Marks */}
                <div className="flex flex-wrap items-center gap-2 mt-3 pl-10">
                  {markLabels.length > 0 ? (
                    markLabels.map((mark, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg"
                      >
                        <span className="font-medium text-slate-600">{mark.label}:</span>
                        <span className="font-bold text-slate-800">{mark.obtained}</span>
                        <span className="text-slate-400">/ {mark.max}</span>
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-400 italic">No marks recorded</span>
                  )}

                  {hasAttendance && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const subjectId = subject.subjectId || subject.id;
                        setExpandedAttendance(isAttendanceExpanded ? null : subjectId);
                      }}
                      className={`text-slate-400 hover:text-indigo-600 bg-slate-100 hover:bg-indigo-50 p-1.5 rounded-lg transition-colors ${
                        isAttendanceExpanded ? 'bg-indigo-50 text-indigo-600' : ''
                      }`}
                      title="View attendance details"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Attendance Expand Section - Inline */}
              {isAttendanceExpanded && hasAttendance && (
                <AttendanceInfoExpand 
                  subject={subject}
                  onClose={() => setExpandedAttendance(null)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-slate-500">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {marksData.subjects.length} Subjects
          </span>
          <span className="hidden sm:inline text-slate-300">|</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {marksData?.lastUpdated ? `Updated: ${new Date(marksData.lastUpdated).toLocaleDateString()}` : 'Official Record'}
          </span>
        </div>
        
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400">CIE: Continuous Internal Evaluation</span>
        </div>
      </div>
    </div>
  );
};

export default ParentMarksView;